/** Finger-drag and tap-to-move for town army sockets.
 *
 * HTML5 drag-and-drop does not run on iOS Safari, and a landscape phone also
 * puts the seven sockets in an `overflow-x: auto` row — so a horizontal pan
 * is a scroll, not a drag. Pointer events with a slop, plus a two-tap path
 * (arm, then drop), are how garrison ↔ visiting-hero transfers work there.
 *
 * Pure, so a test can drive the tap state machine without mounting the bar.
 */

export const TOWN_SOCKET_ATTR = "data-town-socket";

/** CSS pixels of travel before a press on a socket is a drag, not a tap. */
export const TOWN_SOCKET_SLOP_PX = 10;

export interface TownSocketRef {
  readonly army: string;
  readonly slot: number;
}

export function townSocketKey(army: string, slot: number): string {
  return `${army}:${String(slot)}`;
}

export function parseTownSocket(value: string | null | undefined): TownSocketRef | null {
  if (value === null || value === undefined || value.length === 0) return null;
  const i = value.lastIndexOf(":");
  if (i <= 0) return null;
  const army = value.slice(0, i);
  const slot = Number(value.slice(i + 1));
  if (army.length === 0 || !Number.isInteger(slot) || slot < 0 || slot > 6) return null;
  return { army, slot };
}

export function socketFromPoint(x: number, y: number): TownSocketRef | null {
  if (typeof document === "undefined") return null;
  const node = document.elementFromPoint(x, y);
  const el = node instanceof Element ? node.closest(`[${TOWN_SOCKET_ATTR}]`) : null;
  return parseTownSocket(el?.getAttribute(TOWN_SOCKET_ATTR));
}

export function pastSocketSlop(
  start: { readonly x: number; readonly y: number },
  now: { readonly x: number; readonly y: number },
  slop: number = TOWN_SOCKET_SLOP_PX,
): boolean {
  const dx = now.x - start.x;
  const dy = now.y - start.y;
  return (dx * dx + dy * dy) >= slop * slop;
}

export type TownTapAction =
  | { readonly kind: "select"; readonly army: string; readonly slot: number }
  | { readonly kind: "inspect"; readonly army: string; readonly slot: number }
  | { readonly kind: "drop"; readonly army: string; readonly slot: number }
  | { readonly kind: "ignore" };

/**
 * What a tap on a town army socket should do when HTML5 drag is unavailable.
 *
 * First tap on a filled socket arms it. The same socket again inspects (the
 * split / dismiss sheet). Any other socket — empty or not — is the drop.
 */
/** After a finger-drag drop, iOS may synthesize a click on the destination
 *  ~300ms later. Swallow those so the drop is not followed by a select. */
let swallowClicksUntil = 0;

export function markTownPointerDrop(holdMs = 450): void {
  swallowClicksUntil = Date.now() + holdMs;
}

export function resetTownPointerClicks(): void {
  swallowClicksUntil = 0;
}

export function townClickSwallowed(now: number = Date.now()): boolean {
  return now < swallowClicksUntil;
}

export function townSocketTap(args: {
  readonly armed: TownSocketRef | null;
  readonly army: string;
  readonly slot: number;
  readonly filled: boolean;
}): TownTapAction {
  const { armed, army, slot, filled } = args;
  if (armed === null) {
    return filled ? { kind: "select", army, slot } : { kind: "ignore" };
  }
  if (armed.army === army && armed.slot === slot) {
    return { kind: "inspect", army, slot };
  }
  return { kind: "drop", army, slot };
}
